/**
 * Spanish - Latin America & Spain (Windows KBDLA / Unicode CLDR `es-419-t-k0-windows`): maps a
 * physical code to the unshifted character that key emits. TheCore ships one "Latin America and
 * Spain" file; its serialization tokens (witnessed by both hands, see below) match the Latin
 * American virtual-key layout (KBDLA), so these legends are KBDLA's. The letter and digit rows are
 * QWERTY with digits unshifted; the national keys are the enye on the Semicolon code, an acute dead
 * key on BracketLeft, and inverted punctuation on the number row. Where Spain (KBDSP) differs from
 * Latin America the divergence is noted inline; it never changes a serialization token, only the
 * printed legend. Dead keys (acute, grave) are legends only here. The extra ISO key by left Shift
 * (IntlBackslash) is outside the US-Qwerty code block and is not bound by TheCore.
 */
export const SPANISH_CODE_TO_CHAR: Record<string, string> = {
  Backquote: "|", // KBDLA "|/°/¬"; Spain (KBDSP) prints the masculine ordinal "º"
  Digit1: "1",
  Digit2: "2",
  Digit3: "3",
  Digit4: "4",
  Digit5: "5",
  Digit6: "6",
  Digit7: "7",
  Digit8: "8",
  Digit9: "9",
  Digit0: "0",
  Minus: "'", // apostrophe (VK_OEM_4)
  Equal: "¿", // inverted question mark; Spain (KBDSP) prints inverted exclamation "¡"
  KeyQ: "Q",
  KeyW: "W",
  KeyE: "E",
  KeyR: "R",
  KeyT: "T",
  KeyY: "Y",
  KeyU: "U",
  KeyI: "I",
  KeyO: "O",
  KeyP: "P",
  BracketLeft: "´", // dead acute (U+00B4); Spain (KBDSP) puts dead grave "`" here instead
  BracketRight: "+",
  Backslash: "}", // KBDLA "}/]"; Spain (KBDSP) prints "ç" (c-cedilla)
  KeyA: "A",
  KeyS: "S",
  KeyD: "D",
  KeyF: "F",
  KeyG: "G",
  KeyH: "H",
  KeyJ: "J",
  KeyK: "K",
  KeyL: "L",
  Semicolon: "ñ", // enye (VK_OEM_1)
  Quote: "{", // KBDLA "{/["; Spain (KBDSP) puts dead acute "´" here instead
  KeyZ: "Z",
  KeyX: "X",
  KeyC: "C",
  KeyV: "V",
  KeyB: "B",
  KeyN: "N",
  KeyM: "M",
  Comma: ",",
  Period: ".",
  Slash: "-", // hyphen-minus (VK_OEM_MINUS)
};

/**
 * The .SC2Hotkeys serialization token per code where Spanish's token diverges from charToken of its
 * character. SC2 binds these OEM keys by virtual-key, so the file names the key
 * by its Spanish VK, not the symbol it emits. Recovered command-for-command from TheCore's real
 * US-Qwerty -> "Latin America and Spain" pair (both hands): the right-hand TargetChoose key list
 * aligns position by position (US ";"-key -> "Grave", "/"-key -> "Minus", "["-key -> "SemiColon",
 * "-"-key -> "BracketOpen", "]"-key -> "Equals", "="-key -> "BracketClose"). Slash is omitted: its
 * legend "-" already serializes as "Minus" through the char path. Letters and digits are
 * positional/char-path and omitted.
 *
 * Backquote -> "Grave" is intentionally NON-injective: it duplicates the enye key (Semicolon, also
 * VK_OEM_3 -> "Grave"). TheCore never converts the US backtick key for this locale - every left-hand
 * Backquote binding (Hive/Lair, Land, Lift, the SIRoot/SIUproot families, ...) is left verbatim on
 * the US "Grave" token (oracle == seed for all 36), so to reproduce the real file convert must emit
 * "Grave" here too. SC2 reads "Grave" as VK_OEM_3 (the enye key), TheCore's own collision, which the
 * char path could not produce ("|" is not a valid token). The entry is placed last so the inverse
 * parse resolves "Grave" to the enye key (first-seen Semicolon), matching SC2; the cost is that the
 * Backquote position does not round-trip, so Spanish is excluded from the positional round-trip test,
 * like the ISO "Backslash -> Enter" layouts. Portugal has the identical backtick quirk.
 */
export const SPANISH_CODE_TO_TOKEN: Record<string, string> = {
  Minus: "BracketOpen", // ', VK_OEM_4
  Equal: "BracketClose", // ¿ / ¡, VK_OEM_6
  BracketLeft: "SemiColon", // ´ dead, VK_OEM_1
  BracketRight: "Equals", // +, VK_OEM_PLUS
  Backslash: "BackSlash", // } / ç, VK_OEM_5
  Semicolon: "Grave", // ñ, VK_OEM_3
  Quote: "Apostrophe", // { / ´, VK_OEM_7
  Backquote: "Grave", // backtick key left unconverted by TheCore; aliases the enye key (faithful, non-injective)
};
